import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { getForcedLayout } from '@/lib/websiteTemplate';

export type LayoutType = 'layout-1' | 'layout-2' | 'layout-3' | 'layout-4' | 'layout-5' | 'layout-6' | 'layout-7';

export const VALID_LAYOUTS: LayoutType[] = ['layout-1', 'layout-2', 'layout-3', 'layout-4', 'layout-5', 'layout-6', 'layout-7'];

const isValidLayout = (value: unknown): value is LayoutType =>
  VALID_LAYOUTS.includes(value as LayoutType);

interface LayoutContextType {
  currentLayout: LayoutType;
  setLayout: (layout: LayoutType) => Promise<void>;
  defaultLayout: LayoutType;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

const LAYOUT_STORAGE_KEY = 'selected-layout';
const WEBSITE_TEMPLATE_STORAGE_KEY = 'website-template';
const MANUAL_SELECTION_FLAG_KEY = 'layout-manual-selection';
const DEFAULT_LAYOUT: LayoutType = 'layout-1';

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

interface LayoutProviderProps {
  children: ReactNode;
  websiteTemplate?: number;
}

/**
 * Maps websiteTemplate number to layout type
 * 1 = layout-1, 2 = layout-2, etc.
 * Falls back to layout-1 if number is invalid or not provided
 */
const mapWebsiteTemplateToLayout = (templateNumber?: number): LayoutType => {
  if (typeof templateNumber === 'number') {
    // Map number to layout (1 = layout-1, 2 = layout-2, etc.)
    if (templateNumber === 1) return 'layout-1';
    if (templateNumber === 2) return 'layout-2';
    if (templateNumber === 3) return 'layout-3';
    if (templateNumber === 4) return 'layout-4';
    if (templateNumber === 5) return 'layout-5';
    if (templateNumber === 6) return 'layout-6';
    if (templateNumber === 7) return 'layout-7';
  }
  return DEFAULT_LAYOUT;
};

export const LayoutProvider = ({ children, websiteTemplate }: LayoutProviderProps) => {
  const [currentLayout, setCurrentLayout] = useState<LayoutType>(DEFAULT_LAYOUT);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Load layout from storage on mount and when websiteTemplate changes
  useEffect(() => {
    const loadLayout = async () => {
      // DEV OVERRIDE — remove junto com src/lib/websiteTemplate.ts
      const forcedLayout = getForcedLayout();
      if (forcedLayout) {
        setCurrentLayout(forcedLayout);
        setHasInitialized(true);
        setIsLoaded(true);
        return;
      }

      let shouldMarkAsLoaded = true;
            
      try {
        // First, check for temporary layout (from layout change)
        const tempLayout = await secureGetItem('selected-layout-temp');
        const tempLayoutValue = typeof tempLayout === 'string' ? tempLayout : tempLayout?.value || tempLayout;
        
        if (tempLayoutValue && isValidLayout(tempLayoutValue)) {
          // Use temporary layout and save it to permanent storage
          setCurrentLayout(tempLayoutValue as LayoutType);
          await secureSetItem(LAYOUT_STORAGE_KEY, tempLayoutValue);
          // Save current websiteTemplate when using temp layout
          if (websiteTemplate !== undefined) {
            await secureSetItem(WEBSITE_TEMPLATE_STORAGE_KEY, String(websiteTemplate));
          }
          // Check if this was a manual selection (flag should already be set by modal)
          const manualFlag = await secureGetItem(MANUAL_SELECTION_FLAG_KEY);
          const isManual = manualFlag === true || manualFlag === 'true' || (typeof manualFlag === 'object' && manualFlag?.value === true);
          if (!isManual) {
            // If flag not set, set it now (shouldn't happen, but just in case)
            await secureSetItem(MANUAL_SELECTION_FLAG_KEY, true);
          }
          // Clear temporary storage
          await secureSetItem('selected-layout-temp', '');
          setHasInitialized(true);
          setIsLoaded(true);
          return;
        }

        // Wait for websiteTemplate to be available if it's not yet
        if (websiteTemplate === undefined) {
          // Config not loaded yet, wait for it
          // Don't mark as loaded yet, will retry when websiteTemplate becomes available
          shouldMarkAsLoaded = false;
          return;
        }

        // Check if layout was manually selected by user (should ignore config changes)
        const manualSelection = await secureGetItem(MANUAL_SELECTION_FLAG_KEY);
        const isManualSelection = manualSelection === true || 
                                  manualSelection === 'true' || 
                                  (typeof manualSelection === 'object' && manualSelection?.value === true);

        // Get saved websiteTemplate from storage
        const savedTemplate = await secureGetItem(WEBSITE_TEMPLATE_STORAGE_KEY);
        let savedTemplateValue: number | null = null;
        
        if (savedTemplate !== null && savedTemplate !== undefined) {
          if (typeof savedTemplate === 'string') {
            const parsed = parseInt(savedTemplate, 10);
            savedTemplateValue = isNaN(parsed) ? null : parsed;
          } else if (typeof savedTemplate === 'number') {
            savedTemplateValue = savedTemplate;
          } else if (savedTemplate?.value !== undefined) {
            const parsed = parseInt(String(savedTemplate.value), 10);
            savedTemplateValue = isNaN(parsed) ? null : parsed;
          }
        }

        // Normalize websiteTemplate to number for comparison
        const currentTemplate = typeof websiteTemplate === 'number' ? websiteTemplate : null;

        // Check if websiteTemplate changed (compare as numbers)
        const templateChanged = savedTemplateValue !== null && 
                                currentTemplate !== null && 
                                savedTemplateValue !== currentTemplate;
        
        // No temporary layout, load from permanent storage
        const savedLayout = await secureGetItem(LAYOUT_STORAGE_KEY);
        // Ensure we have a valid layout string
        const layoutValue = typeof savedLayout === 'string' ? savedLayout : savedLayout?.value || savedLayout;
        
        if (layoutValue && isValidLayout(layoutValue)) {
          // If layout was manually selected, ignore config changes and use saved layout
          if (isManualSelection) {
            setCurrentLayout(layoutValue as LayoutType);
            setHasInitialized(true);
            setIsLoaded(true);
            return;
          }

          // Calculate what layout should be based on current template
          const expectedLayout = currentTemplate !== null 
            ? mapWebsiteTemplateToLayout(currentTemplate) 
            : null;
          
          // Check if current layout matches expected layout for current template
          const layoutMatchesTemplate = expectedLayout !== null && layoutValue === expectedLayout;
          
          // User has previously selected a layout
          if (templateChanged && currentTemplate !== null) {
            // Template changed in config, update to new template layout
            const newLayout = mapWebsiteTemplateToLayout(currentTemplate);
            setCurrentLayout(newLayout);
            await secureSetItem(LAYOUT_STORAGE_KEY, newLayout);
            await secureSetItem(WEBSITE_TEMPLATE_STORAGE_KEY, String(currentTemplate));
            setHasInitialized(true);
            setIsLoaded(true);
            return;
          } else if (!layoutMatchesTemplate && currentTemplate !== null && expectedLayout !== null) {
            // Layout doesn't match current template (template may have changed but wasn't saved, or storage was cleared)
            setCurrentLayout(expectedLayout);
            await secureSetItem(LAYOUT_STORAGE_KEY, expectedLayout);
            await secureSetItem(WEBSITE_TEMPLATE_STORAGE_KEY, String(currentTemplate));
            setHasInitialized(true);
            setIsLoaded(true);
            return;
          } else {
            // Template unchanged and layout matches, use saved layout
            setCurrentLayout(layoutValue as LayoutType);
            // Update saved template if not set or if it's different (sync)
            if (savedTemplateValue === null || (currentTemplate !== null && savedTemplateValue !== currentTemplate)) {
              if (currentTemplate !== null) {
                await secureSetItem(WEBSITE_TEMPLATE_STORAGE_KEY, String(currentTemplate));
              }
            }
            setHasInitialized(true);
            setIsLoaded(true);
            return;
          }
        }

        // No saved layout - need to use default from config.websiteTemplate
        // websiteTemplate is available, use it as default
        const defaultLayout = mapWebsiteTemplateToLayout(websiteTemplate);
        setCurrentLayout(defaultLayout);
        // Save the default layout and template to storage so it persists
        await secureSetItem(LAYOUT_STORAGE_KEY, defaultLayout);
        await secureSetItem(WEBSITE_TEMPLATE_STORAGE_KEY, String(websiteTemplate));
        setHasInitialized(true);
      } catch (error) {
        console.error('Error loading layout:', error);
        const defaultLayout = mapWebsiteTemplateToLayout(websiteTemplate);
        setCurrentLayout(defaultLayout);
        setHasInitialized(true);
      } finally {
        if (shouldMarkAsLoaded) {
          setIsLoaded(true);
        }
      }
    };

    loadLayout();
  }, [websiteTemplate]);

  const setLayout = async (layout: LayoutType, isManualSelection = false) => {
    try {
      await secureSetItem(LAYOUT_STORAGE_KEY, layout);
      // Save current websiteTemplate when user manually changes layout
      if (websiteTemplate !== undefined) {
        await secureSetItem(WEBSITE_TEMPLATE_STORAGE_KEY, String(websiteTemplate));
      }
      // Mark as manual selection if user chose it manually
      if (isManualSelection) {
        await secureSetItem(MANUAL_SELECTION_FLAG_KEY, true);
      }
      setCurrentLayout(layout);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('layoutChanged', { detail: layout }));
    } catch (error) {
      console.error('Error saving layout:', error);
    }
  };

  // Don't render children until layout is loaded
  if (!isLoaded) {
    return null;
  }

  // Calculate default layout for context value
  const defaultLayout = mapWebsiteTemplateToLayout(websiteTemplate);

  return (
    <LayoutContext.Provider value={{ currentLayout, setLayout, defaultLayout }}>
      {children}
    </LayoutContext.Provider>
  );
};

